import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

// Interface for Sentry configuration (for type safety)
export interface SentryConfig {
  dsn: string;
  enabled: boolean;
  environment: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  debug: boolean;
}

// List of sensitive data patterns to scrub
const PII_PATTERNS = [
  /password/i,
  /token/i,
  /authorization/i,
  /api[-_]?key/i,
  /secret/i,
  /credit[-_]?card/i,
  /card[-_]?number/i,
  /cvv/i,
  /ssn/i,
  /national[-_]?id/i,
  /iqama/i,
  /passport/i,
  /phone/i,
  /mobile/i,
  /email/i,
  /address/i,
  /bank[-_]?account/i,
  /iban/i,
  /swift/i,
];

// List of URL patterns that might contain sensitive data
const SENSITIVE_URL_PATTERNS = [
  /\/api\/.*token/i,
  /\/api\/.*auth/i,
  /\/api\/.*login/i,
  /\/api\/.*password/i,
];

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  private static initialized = false;

  constructor(private readonly router: Router) {}

  /**
   * Initialize Sentry with environment-specific configuration
   */
  static init(): void {
    if (SentryService.initialized) {
      return;
    }

    const config = environment.sentry as SentryConfig;

    if (!config.enabled || !config.dsn) {
      console.log('Sentry is disabled or DSN not configured');
      return;
    }

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      debug: config.debug,

      // Performance monitoring
      tracesSampleRate: config.tracesSampleRate,

      // Release tracking - set via CI/CD
      release: (globalThis as any).__SENTRY_RELEASE__ || `investor-web@${environment.name}`,

      // Integration configurations
      integrations: [
        Sentry.browserTracingIntegration(),
      ],

      // Scrub sensitive data before sending
      beforeSend(event, hint) {
        return SentryService.scrubSensitiveData(event);
      },

      // Filter breadcrumbs for sensitive data
      beforeBreadcrumb(breadcrumb) {
        return SentryService.scrubBreadcrumb(breadcrumb);
      },

      // Don't send events in development if debug is enabled but sending is disabled
      enabled: config.enabled,

      // Additional context
      initialScope: {
        tags: {
          'app.name': 'SKFH-InvestorWeb',
          'app.environment': config.environment,
        },
      },
    });

    SentryService.initialized = true;
    console.log(`Sentry initialized for environment: ${config.environment}`);
  }

  /**
   * Set user context for Sentry events
   */
  setUserContext(userId: string | null, email?: string, tenantId?: string): void {
    if (userId) {
      Sentry.setUser({
        id: userId,
        // Don't include email to avoid PII - only set if explicitly needed
        ...(email && { email: SentryService.maskEmail(email) }),
      });

      if (tenantId) {
        Sentry.setTag('tenant.id', tenantId);
      }
    } else {
      Sentry.setUser(null);
    }
  }

  /**
   * Set a correlation ID for distributed tracing
   */
  setCorrelationId(correlationId: string): void {
    Sentry.setTag('correlation.id', correlationId);
  }

  /**
   * Add custom tags to Sentry context
   */
  setTags(tags: Record<string, string>): void {
    Object.entries(tags).forEach(([key, value]) => {
      Sentry.setTag(key, value);
    });
  }

  /**
   * Capture a custom error with additional context
   */
  captureError(error: Error, context?: Record<string, any>): void {
    if (context) {
      Sentry.setContext('additional', context);
    }
    Sentry.captureException(error);
  }

  /**
   * Capture a custom message
   */
  captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
    Sentry.captureMessage(message, level);
  }

  /**
   * Clear user context (on logout)
   */
  clearUserContext(): void {
    Sentry.setUser(null);
  }

  /**
   * Scrub sensitive data from Sentry events
   */
  private static scrubSensitiveData(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
    // Scrub request data
    if (event.request) {
      // Scrub headers
      if (event.request.headers) {
        event.request.headers = SentryService.scrubObject(event.request.headers);
      }

      // Scrub query string
      if (event.request.query_string) {
        event.request.query_string = '[FILTERED]';
      }

      // Scrub cookies
      if (event.request.cookies) {
        event.request.cookies = { filtered: '[FILTERED]' };
      }

      // Scrub request body/data
      if (event.request.data) {
        event.request.data = SentryService.scrubObject(event.request.data);
      }

      // Check if URL contains sensitive patterns
      if (event.request.url) {
        for (const pattern of SENSITIVE_URL_PATTERNS) {
          if (pattern.test(event.request.url)) {
            event.request.url = event.request.url.replace(/\?.*$/, '?[FILTERED]');
            break;
          }
        }
      }
    }

    // Scrub extra context
    if (event.extra) {
      event.extra = SentryService.scrubObject(event.extra);
    }

    // Scrub contexts
    if (event.contexts) {
      event.contexts = SentryService.scrubObject(event.contexts as Record<string, any>) as typeof event.contexts;
    }

    return event;
  }

  /**
   * Scrub sensitive data from breadcrumbs
   */
  private static scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
    // Scrub XHR/fetch breadcrumbs that might contain sensitive URLs
    if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
      const url = breadcrumb.data?.['url'] as string | undefined;
      if (url) {
        for (const pattern of SENSITIVE_URL_PATTERNS) {
          if (pattern.test(url)) {
            breadcrumb.data!['url'] = '[FILTERED_URL]';
            if (breadcrumb.data!['body']) {
              breadcrumb.data!['body'] = '[FILTERED]';
            }
            break;
          }
        }
      }
    }

    // Scrub form data breadcrumbs
    if (breadcrumb.category === 'ui.input') {
      if (breadcrumb.message) {
        breadcrumb.message = '[USER_INPUT]';
      }
    }

    return breadcrumb;
  }

  /**
   * Recursively scrub sensitive data from objects
   */
  private static scrubObject(obj: Record<string, any>): Record<string, any> {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    const scrubbed: Record<string, any> = Array.isArray(obj) ? [] : {};

    for (const key of Object.keys(obj)) {
      const value = obj[key];

      // Check if key matches sensitive patterns
      const isSensitive = PII_PATTERNS.some(pattern => pattern.test(key));

      if (isSensitive) {
        scrubbed[key] = '[FILTERED]';
      } else if (typeof value === 'object' && value !== null) {
        scrubbed[key] = SentryService.scrubObject(value);
      } else if (typeof value === 'string') {
        // Check if value looks like it contains sensitive data
        scrubbed[key] = SentryService.scrubString(value);
      } else {
        scrubbed[key] = value;
      }
    }

    return scrubbed;
  }

  /**
   * Scrub potentially sensitive strings
   */
  private static scrubString(value: string): string {
    // Mask JWT tokens
    if (/^eyJ[A-Za-z0-9-_]+\.eyJ[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.exec(value)) {
      return '[JWT_TOKEN]';
    }

    // Mask credit card numbers (basic pattern)
    if (/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/.exec(value)) {
      return value.replace(/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?(\d{4})\b/, '****-****-****-$1');
    }

    // Mask email addresses
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.exec(value)) {
      return SentryService.maskEmail(value);
    }

    return value;
  }

  /**
   * Mask email addresses for privacy
   */
  private static maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    if (local.length <= 2) {
      return `**@${domain}`;
    }
    return `${local[0]}***${local.at(-1)}@${domain}`;
  }
}

/**
 * Custom ErrorHandler that reports errors to Sentry
 */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}

  handleError(error: any): void {
    const extractedError = error.originalError || error;

    // Log to console in development
    if (environment.sentry?.debug) {
      console.error('Error caught by SentryErrorHandler:', extractedError);
    }

    // Report to Sentry
    Sentry.captureException(extractedError);
  }
}
